import React from 'react';

export default function Grid({
  theme = 'dark',
  images = [],                    // can be: string[] OR {src, alt, title}[]
  gap = 8,
  showTitles = false,
  titleFontPx = 11,
  cellScale = 0.82,
  sizeBasis = 'width',            // 'width' (square by width) or 'height'
  // animation
  animate = true,
  appearDurationMs = 420,
  appearStaggerMs = 70,
  appearFrom = 'up',
  // NEW: per-cell overlay renderer
  getOverlay = null,              // function (index, resolvedImage) => JSX
}) {
  const isDark = theme === 'dark';
  const fallbackA = isDark ? '#111827' : '#F3F4F6';
  const fallbackB = isDark ? '#1F2937' : '#E5E7EB';
  const titleColor = '#fff';

  const clampedScale = Math.max(0.5, Math.min(1, cellScale));
  const pct = `${Math.round(clampedScale * 100)}%`;

  // Render all provided items
  const items = Array.isArray(images) ? images.filter((it) => it != null) : [];

  // Resolve string | object -> { src, alt, title }
  const resolveImage = (it, idx) => {
    if (!it) return null;
    if (typeof it === 'string') {
      return { src: it, alt: `image ${idx + 1}` };
    }
    if (typeof it === 'object' && it.src) return it;
    // Some bundlers import as default export object with .default
    if (typeof it === 'object' && it.default) return { src: it.default, alt: `image ${idx + 1}` };
    return null;
  };

  // simple mount gate for nice entrance animation
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (media && media.matches) { setMounted(true); return; }
    const id = setTimeout(() => setMounted(true), 20);
    return () => clearTimeout(id);
  }, []);

  const fromTransform = React.useMemo(() => {
    switch (appearFrom) {
      case 'down':  return 'translateY(-8px)';
      case 'left':  return 'translateX(8px)';
      case 'right': return 'translateX(-8px)';
      case 'scale': return 'scale(0.96)';
      case 'up':
      default:      return 'translateY(8px)';
    }
  }, [appearFrom]);

  const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';

  return (
    <div
      className="grid grid-cols-4 md:grid-cols-6 w-full place-items-center"
      style={{ gap }}
    >
      {items.map((raw, i) => {
        const img = resolveImage(raw, i);
        const delay = `${i * appearStaggerMs}ms`;
        const transition = `opacity ${appearDurationMs}ms ${easing}, transform ${appearDurationMs}ms ${easing}`;
        const baseStyle = animate
          ? (mounted
              ? { opacity: 1, transform: 'none', transition, transitionDelay: delay, willChange: 'opacity, transform' }
              : { opacity: 0, transform: fromTransform })
          : undefined;

        return (
          <div key={i} className="relative flex items-center justify-center w-full" style={baseStyle}>
            {/* Square sized by width so height follows (aspect-square) */}
            <div
              className="relative aspect-square overflow-hidden rounded-xl"
              style={ sizeBasis === 'width' ? { width: pct } : { height: pct } }
            >
              {img?.src ? (
                <>
                  <img
                    src={img.src}
                    alt={img.alt ?? `image ${i + 1}`}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                    draggable={false}
                  />
                  {showTitles && (img.title || img.alt) && (
                    <div
                      className="absolute bottom-1 left-1 right-1 px-1 py-0.5 rounded bg-black/35"
                      style={{ fontSize: titleFontPx, color: titleColor }}
                    >
                      {img.title || img.alt}
                    </div>
                  )}
                  {typeof getOverlay === 'function' && (
                    <div className="absolute inset-0 pointer-events-none">
                      {getOverlay(i, img)}
                    </div>
                  )}
                </>
              ) : (
                <div
                  className="absolute inset-0"
                  style={{ background: `linear-gradient(135deg, ${fallbackA}, ${fallbackB})` }}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
